import { cn } from '@strivelabs/ui-core/lib/utils';
import { STATUS_META } from './statusMeta';
import type { TaskStatus } from '@/types/task';

/**
 * The status marks, verbatim from the artifact's task list (`TK_STATUS`): one
 * geometry — a circle on the same 24 grid at the same stroke — so the five
 * read as one family, with the semantic colour on the icon alone. An empty
 * ring for work not begun, a half-filled disc for work under way, a question
 * mark for work waiting on somebody, a cross for work dropped; only the
 * terminal success state earns a solid disc.
 */
const STATUS_MARK: Record<TaskStatus, React.ReactNode> = {
  PENDING: <circle cx="12" cy="12" r="8" />,
  IN_PROGRESS: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 6.5A5.5 5.5 0 0 0 12 17.5Z" fill="currentColor" stroke="none" />
    </>
  ),
  IN_REVIEW: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M10.34 10.17a1.83 1.83 0 1 1 2.46 1.71c-.48.22-.8.54-.8 1.09v.17" />
      <path d="M12 15.83h.01" />
    </>
  ),
  COMPLETED: (
    <>
      <circle cx="12" cy="12" r="8.8" fill="currentColor" stroke="none" />
      <path d="M7.85 12.25l2.89 2.83 5.53-5.79" stroke="#FFFFFF" strokeWidth="2" />
    </>
  ),
  CANCELLED: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M9.6 9.6l4.8 4.8M14.4 9.6l-4.8 4.8" />
    </>
  ),
};

/** The glyph alone, for the places the label would repeat what is beside it. */
export const TaskStatusIcon = ({ status, className }: {
  status: TaskStatus;
  className?: string;
}) => (
  <svg
    viewBox="3.2 3.2 17.6 17.6"
    className={cn('size-4 shrink-0 fill-none stroke-current stroke-[1.6]', className)}
    style={{ color: STATUS_META[status]?.ink }}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {STATUS_MARK[status] ?? STATUS_MARK.PENDING}
  </svg>
);

interface TaskStatusCellProps {
  status: TaskStatus;
  className?: string;
}

/**
 * A task's status, as the table, the panel and the filter menu all show it.
 *
 * One cell for the three, because a status that reads differently in the menu
 * than in the row it filters is a status the reader has to translate. In all
 * of them a status is a system indicator rather than a tag (artifact
 * `.st-cell`): no pill, no background, no border — the semantic colour lives
 * on the icon alone while the label stays in the board's own ink.
 */
export const TaskStatusCell = ({ status, className }: TaskStatusCellProps) => (
  <span
    className={cn(
      'inline-flex min-w-0 items-center gap-[7px] whitespace-nowrap text-[13.5px] text-[#181818]',
      className,
    )}
  >
    <TaskStatusIcon status={status} />
    {STATUS_META[status]?.label ?? status}
  </span>
);
